///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One shared poll of DatasetLayerManager.getLoadStates() for every
/// loading indicator (Layers button, layer rows, legend chips, timeline),
/// with the display hysteresis applied once here so they all agree:
///
/// - show delay: a wait shorter than cShowDelayMs never flashes a ring
///   (cache hits and small deltas finish well inside it)
/// - minimum visible: once shown, a ring stays for cMinVisibleMs so a
///   burst of short loads doesn't flicker
/// - completion beat: on finishing, the ring is briefly full (`done`)
///   before it fades, timed to the marks landing on the globe
///
/// The poll runs only while something subscribes, fast while anything is
/// active or winding down, slow when idle.
///

import { type LayerLoadReport } from "@/earthbrowser/datasets/LoadState"
import { type TimeZoneMode } from "@/redux/earthBrowserSlice"
import { appStore } from "@/redux/store"
import type { DatasetType } from "@/types/DatasetElement"
import { formatTickTime } from "@/utils/timeControl"
import { useSyncExternalStore } from "react"

export interface DisplayedLoadState extends LayerLoadReport {
	/** Layers-panel / visibility type this engine layer maps to (null for unlisted layers) */
	type: DatasetType | null
	/** The wait just ended: hold a full ring for the completion beat, then drop */
	done: boolean
}

const cShowDelayMs = 350
const cMinVisibleMs = 600
const cDoneBeatMs = 250
const cActivePollMs = 250
const cIdlePollMs = 1500

// Engine layer names (DatasetLayer.name / external provider ids) to the
// DatasetType that toggles them in the Layers panel
const cLayerTypes: Record<string, DatasetType> = {
	earthquakes: "earthquake",
	"earthquake-history": "earthquake",
	slab2: "earthquake",
	buoys: "buoy",
	webcams: "webcam",
	locations: "location",
	aurora: "aurora",
	radar: "radar",
	radar_forecast: "radar_forecast",
	cloud_forecast: "cloud_forecast",
	nexrad: "nexrad",
	clouds: "cloud",
	imerg: "precipitation",
	mrms: "precipitation",
	sst: "sst",
	satellites: "satellite",
	wildfires: "wildfire",
	hurricanes: "hurricane",
	alerts: "alert",
	volcanoes: "volcano",
	synoptic: "synoptic",
	placemarks: "placemark",
}

interface Tracked {
	/** When the layer first had something pending (engine `since` when earlier than our first sight) */
	activeSince: number
	/** When the indicator first showed; null while inside the show delay */
	shownAt: number | null
	/** When the layer last reported nothing pending; null while active */
	endedAt: number | null
	last: LayerLoadReport
}

const tracked = new Map<string, Tracked>()
const listeners = new Set<() => void>()
let snapshot: DisplayedLoadState[] = []
let snapshotKey = ""
let timer: number | null = null

/** Layers-panel type an engine layer name belongs to (null for unlisted layers) */
export function datasetTypeForLayerId(id: string): DatasetType | null {
	return cLayerTypes[id] ?? null
}

function display(report: LayerLoadReport, done: boolean): DisplayedLoadState {
	return { ...report, type: datasetTypeForLayerId(report.id), done }
}

function poll(): void {
	timer = null
	const now = performance.now()
	const reports = appStore.getState().datasetScene.layerManager?.getLoadStates() ?? []

	const active = new Set<string>()
	for (const report of reports) {
		active.add(report.id)
		const entry = tracked.get(report.id)
		if (entry) {
			entry.last = report
			// activity resumed before the indicator finished winding down
			entry.endedAt = null
		} else {
			tracked.set(report.id, { activeSince: Math.min(now, report.since), shownAt: null, endedAt: null, last: report })
		}
	}

	const next: DisplayedLoadState[] = []
	for (const [id, entry] of tracked) {
		const isActive = active.has(id)
		if (!isActive && entry.endedAt === null) {
			entry.endedAt = now
		}
		if (entry.shownAt === null) {
			if (!isActive) {
				// finished inside the show delay: never flash
				tracked.delete(id)
				continue
			}
			if (now - entry.activeSince < cShowDelayMs) continue
			entry.shownAt = now
		}
		if (isActive) {
			next.push(display(entry.last, false))
			continue
		}
		const holdUntil = Math.max(entry.shownAt + cMinVisibleMs, entry.endedAt!)
		if (now < holdUntil) {
			next.push(display(entry.last, false))
		} else if (now < holdUntil + cDoneBeatMs) {
			next.push(display(entry.last, true))
		} else {
			tracked.delete(id)
		}
	}

	// Only publish a new array when something the UI shows changed, so
	// useSyncExternalStore subscribers don't re-render every poll
	const key = next.map(s => `${s.id}|${s.visible ? 1 : 0}|${s.phase}|${s.progress?.toFixed(2) ?? ""}|${s.detail ?? ""}|${s.atMs ?? ""}|${s.done ? 1 : 0}`).join(";")
	if (key !== snapshotKey) {
		snapshotKey = key
		snapshot = next
		for (const listener of listeners) listener()
	}

	if (listeners.size > 0) {
		timer = window.setTimeout(poll, tracked.size > 0 ? cActivePollMs : cIdlePollMs)
	}
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener)
	if (timer === null) {
		timer = window.setTimeout(poll, 0)
	}
	return () => {
		listeners.delete(listener)
		if (listeners.size === 0 && timer !== null) {
			window.clearTimeout(timer)
			timer = null
		}
	}
}

const getSnapshot = () => snapshot
const cEmpty: DisplayedLoadState[] = []
const getServerSnapshot = () => cEmpty

/** Every layer with a loading indicator to show right now (after hysteresis) */
export function useLayerLoadStates(): DisplayedLoadState[] {
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * The indicator for one Layers-panel type, or null. Several engine layers
 * can map to one type (precipitation = IMERG + MRMS, earthquakes + their
 * history); the one on the globe wins, then the earliest phase
 */
export function useLayerLoadState(type: DatasetType | undefined): DisplayedLoadState | null {
	const states = useLayerLoadStates()
	if (!type) return null
	let best: DisplayedLoadState | null = null
	for (const state of states) {
		if (state.type !== type) continue
		if (!best || (state.visible && !best.visible)) best = state
	}
	return best
}

/** Short status for a layer row or tooltip: "Downloading 2.1 MB", "Building 1,200 / 4,800", "Loading Tue 14:30 frame" */
export function loadStateLabel(state: DisplayedLoadState, timeZoneMode: TimeZoneMode): string {
	if (state.done) return "Loaded"
	switch (state.phase) {
		case "error": return state.detail ?? "Retrying"
		case "downloading": return state.detail ? `Downloading ${state.detail}` : "Downloading"
		case "ingesting": return state.detail ?? "Processing"
		case "building": return state.detail ? `Building ${state.detail}` : "Building"
		case "frames": return state.atMs !== undefined ? `Loading ${formatTickTime(state.atMs, timeZoneMode)} frame` : "Loading frame"
	}
}
